import { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useFormik } from 'formik';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';

import { SubmitButton } from './components/SubmitButton';
import { ErrorMessage } from './components/ErrorMessage';
import { StoreSection } from './components/StoreSection';
import { BirthdateInput } from './components/BirthdateInput';
import { NameSection } from './components/NameSection';
import { AddressSection } from './components/AddressSection';
import { DividerLine } from './components/DividerLine';
import { RelocationDateInput } from './components/RelocationDateInput';
import { RelocationAddressSection } from './components/RelocationAddressSection';
import { breakpoints, initialValues, validationSchema } from './constants';
import { TFormValues } from './types';

const Footnote = styled.div``;

interface IProps {
    className?: string;
}

const BaseApp = ({ className }: IProps) => {
    const [error, setError] = useState('');

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (formValues) => {
            if (!process.env.REACT_APP_MAILER_API_URL) return;

            const postBody = {
                substitutionData: {
                    ...formValues,
                },
                company: 'pnl',
                useEuAccount: true,
            };

            const isProduction =
                process.env.NODE_ENV === 'production' &&
                process.env.REACT_APP_IS_STAGING !== 'true';

            // Send mail to store(s) only in production build
            const storeEmail = isProduction
                ? formValues.storeEmail
                : process.env.REACT_APP_TEST_EMAIL;

            const storeTwoEmail = isProduction ? formValues.storeTwoEmail : '';

            const customerServiceBody = {
                ...postBody,
                email: storeEmail,
                bcc: storeTwoEmail,
                templateId: 'pnl---verhuisformulier---klantenservice',
            };

            const customerBody = {
                ...postBody,
                email: formValues.customerEmail,
                templateId: 'pnl---verhuisformulier---klant',
            };

            try {
                // Send customer service email
                await axios.post(
                    process.env.REACT_APP_MAILER_API_URL,
                    customerServiceBody
                );

                // Send customer email
                await axios.post(
                    process.env.REACT_APP_MAILER_API_URL,
                    customerBody
                );

                const redirectObj = window.top ?? window;
                redirectObj.location.href =
                    'https://www.pearle.nl/service/bedankt-verhuizing';
            } catch {
                setError(
                    'Er is een fout opgetreden bij het vesturen van het formulier'
                );
            }
        },
    });

    const {
        values: {
            customerEmail,
            birthdate,
            relocationDate,
            phoneNumber,
            termsAgreed,
        },
        touched,
        errors: formikErrors,
        handleChange,
        handleSubmit,
        setFieldValue,
    } = formik;

    const getFieldError = (fieldName: keyof TFormValues) => {
        if (touched[fieldName] && formikErrors[fieldName]) {
            return formikErrors[fieldName];
        }

        return '';
    };

    return (
        <div className={className}>
            <form onSubmit={handleSubmit}>
                <h3>Je gegevens</h3>
                <NameSection
                    formValues={formik.values}
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                />
                <BirthdateInput
                    birthdate={birthdate}
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                />
                <TextField
                    error={!!getFieldError('phoneNumber')}
                    helperText={getFieldError('phoneNumber')}
                    id="phoneNumber"
                    label="Telefoonnummer *"
                    value={phoneNumber}
                    onChange={handleChange}
                />
                <TextField
                    error={!!getFieldError('customerEmail')}
                    helperText={getFieldError('customerEmail')}
                    id="customerEmail"
                    label="E-mailadres *"
                    type="email"
                    value={customerEmail}
                    onChange={handleChange}
                />
                <h3>Bij welke Pearle winkel(s) ben je klant?</h3>
                <StoreSection
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                    storeEmailField="storeEmail"
                    storeField="store"
                />
                <StoreSection
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                    storeEmailField="storeTwoEmail"
                    storeField="storeTwo"
                />
                <h3>Maak je gebruik van het contactlenzenabonnement?</h3>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="sendToDeliveryService"
                            onChange={handleChange}
                        />
                    }
                    label="Ja."
                />
                <h3>Verhuisdatum</h3>
                <RelocationDateInput
                    getFieldError={getFieldError}
                    relocationDate={relocationDate}
                    setFieldValue={setFieldValue}
                />
                <h3>Oud adres</h3>
                <AddressSection
                    formValues={formik.values}
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                />
                <h3>Nieuw adres</h3>
                <RelocationAddressSection
                    formValues={formik.values}
                    getFieldError={getFieldError}
                    setFieldValue={setFieldValue}
                />
                <Footnote>* Verplicht in te vullen</Footnote>
                <DividerLine />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={termsAgreed}
                            id="termsAgreed"
                            onChange={handleChange}
                        />
                    }
                    label={
                        <>
                            Door het invullen van dit formulier ga ik akkoord
                            met de{' '}
                            <a
                                href="https://www.pearle.nl/home/algemene-voorwaarden"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Algemene Voorwaarden
                            </a>{' '}
                            en de{' '}
                            <a
                                href="https://www.pearle.nl/home/disclaimer-privacy"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Privacy Statement
                            </a>
                            .
                        </>
                    }
                />
                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : (
                    <SubmitButton disabled={!termsAgreed}>
                        Versturen
                    </SubmitButton>
                )}
            </form>
        </div>
    );
};

export const App = styled(BaseApp)`
    ${Footnote} {
        font-size: 14px;
        margin-top: 20px;
    }

    // Styles matching the Pearle environment
    padding: 0 32px 100px;

    @media (${breakpoints.largeMin}) {
        padding: 0 0 100px;
        margin: 0 auto;
        max-width: 920px;
    }
`;
